import * as React from 'react'
import handleViewport from 'react-in-viewport'
import getConfig from 'next/config'

import {
  useLogger,
  useSessionSettings,
  useSiteConfig,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import {
  NextConfig,
  QubitMode,
} from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { ESIComponent, withESIWrapper } from '@thg-commerce/enterprise-esi'
import { ESIRequestContext } from '@thg-commerce/enterprise-esi/src/types'
import { pushToEventGA4 } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import { ProductBlockListData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/ProductBlockList'
import {
  PlacementContentArgs,
  PlacementContentType,
  PreviewOptions,
  QubitViewType,
  RecommendationsContent,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Qubit'
import {
  Country,
  Currency,
  Feature,
  ProductBlockListQueryVariables,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { PlacementContent as QUBIT_QUERY } from '@thg-commerce/enterprise-network/src/graphql/Query/Qubit/PlacementContent.graphql'
import { spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import { Grid, GridItem } from '@thg-commerce/gravity-system'

import { removeCurrencySymbol } from '../../Price'
import {
  sendImpression,
  sendPlacementImpressionEvents,
} from '../qubitImpressions'

import { fetchFallbackData } from './Fallback'
import { ProductRecommendationRail } from './ProductRecommendationRail'
import { QubitProducts as QUBIT_PRODUCTS_QUERY } from './QubitProducts.graphql'
import { RecommendationSource } from './types'

interface QubitReccommendationProps {
  placementId: string
  mode: QubitMode
  previewOptions?: PreviewOptions
  subTypes?: string[]
  itemColSpan?: number[]
  title?: string
  source: RecommendationSource
  product?: {
    sku: number
    title: string
  }
}

interface QubitRecommendationsInitialProps {
  products?: ProductBlockListData
  qubitCallbackData?: string
}

interface ProductBlockListFeatureArgs {
  vipPriceEnabled?: boolean
  productContentKeys?: string[]
}

const Title = styled.h2`
  ${Text('xlarge1', 'alternate')};
  text-align: center;
  padding: ${spacing(4)} 0;
`

const ProductsContainer = ({
  forwardedRef,
  children,
}: {
  forwardedRef: React.RefObject<HTMLDivElement>
  children: React.ReactNode
}) => {
  return (
    <div style={{ display: 'grid', columnSpan: 'all' }} ref={forwardedRef}>
      <Grid>{children}</Grid>
    </div>
  )
}

const processedGA4Data = (
  products: ProductBlockListData = [],
  useExternalIdentifier: boolean = false,
  useExternalIdentifierInSchema: boolean = false,
  widgetTitle: string = '',
) => {
  return products?.map((item) => ({
    item_id:
      (useExternalIdentifier || useExternalIdentifierInSchema) &&
      item.externalIdentifier
        ? item.externalIdentifier
        : item.sku,
    item_name: item.title,
    item_brand: item.brand?.name || '',
    category: item.category,
    price: removeCurrencySymbol(item?.price?.defaultPrice || ''),
    quantity: 1,
    item_list_name: widgetTitle,
  }))
}

const QubitRecommendations: ESIComponent<
  QubitReccommendationProps & QubitRecommendationsInitialProps,
  QubitRecommendationsInitialProps
> = (props) => {
  const siteConfig = useSiteConfig()
  const sessionSettings = useSessionSettings()
  const logger = useLogger()
  const impressionEmittedRef = React.useRef(false)

  const ProductsContainerInViewPort = React.useMemo(
    () => handleViewport(ProductsContainer, {}, { disconnectOnLeave: true }),
    [],
  )

  React.useEffect(() => {
    if (siteConfig.useGA4EnhancedEcom) {
      pushToEventGA4({
        event: 'ecom_event',
        event_name: 'view_item_list',
        ecommerce: {
          currencyCode: sessionSettings?.currency,
          items: processedGA4Data(
            props.products,
            siteConfig.useExternalIdentifier,
            siteConfig.useExternalIdentifierInSchema,
            props.title,
          ),
        },
      })
    }
  }, [
    props.products,
    props.title,
    sessionSettings?.currency,
    siteConfig.useExternalIdentifier,
    siteConfig.useExternalIdentifierInSchema,
    siteConfig.useGA4EnhancedEcom,
  ])

  const onEnterViewport = () => {
    if (impressionEmittedRef.current) {
      return
    }
    sendPlacementImpressionEvents({
      logger,
      sendPlacementLevelImpression: sendImpression,
      productId: props.products?.map((product) => product.sku),
      callbackData: props.qubitCallbackData,
      callbackURL: siteConfig.qubit?.callbackURL,
    })
    impressionEmittedRef.current = true
  }

  const products = React.useMemo(() => {
    return props.products
      ? props.products?.map((product, index) => (
          <GridItem
            key={index}
            colSpan={props.itemColSpan ? props.itemColSpan : [6, 3, 3, 3]}
          >
            <ProductRecommendationRail
              product={product}
              qubitCallbackData={props.qubitCallbackData || ''}
            />
          </GridItem>
        ))
      : null
  }, [props.itemColSpan, props.products, props.qubitCallbackData])

  if (!siteConfig.qubit?.enabled || !props.products?.length) {
    return null
  }

  return (
    <React.Fragment>
      {props.title ? <Title>{props.title}</Title> : undefined}
      <ProductsContainerInViewPort onEnterViewport={onEnterViewport}>
        {products}
      </ProductsContainerInViewPort>
    </React.Fragment>
  )
}

QubitRecommendations.getInitialProps = withCacheConfiguration<
  QubitRecommendationsInitialProps,
  ESIRequestContext<
    QubitRecommendationsInitialProps & QubitReccommendationProps
  >
>(async (context) => {
  if (context.props.products) {
    return {
      products: context.props.products,
      qubitCallbackData: context.props.qubitCallbackData,
      cache: context.props.cache,
    }
  }
  try {
    if (!context.req.cookies['_qubitTracker']) {
      return {
        ...context.props,
        products: await fetchFallbackData(
          context.props.source,
          context.apolloClient,
        ),
        qubitCallbackData: undefined,
        cache: {
          ttl: 0,
        },
      }
    }

    const defaultLocale = context.config.publicRuntimeConfig.siteDefinition.defaultLocale
      .replace(/_/g, '-')
      .toLowerCase()

    const currency =
      context.req.config.sessionSettings?.currency ||
      context.config.publicRuntimeConfig.siteDefinition.defaultCurrency

    const { data: qubitData } = await context.apolloClient.query<
      {
        qubitPlacements: {
          callbackData: string
          content: {
            [PlacementContentType.RECOMMENDATIONS]: RecommendationsContent
          }
        }
      },
      PlacementContentArgs
    >({
      query: QUBIT_QUERY,
      variables: {
        placementId: context.props.placementId,
        mode: context.props.mode,
        previewOptions: {
          experienceId: context.props.previewOptions?.experienceId,
          campaignId: context.props.previewOptions?.campaignId,
          group: context.props.previewOptions?.group,
        },
        attributes: {
          visitor: {
            id: context.req.cookies['_qubitTracker'],
            url: `https://${context.config.publicRuntimeConfig.siteDefinition.domain}${context.req.browserUrl}`,
          },
          user: {},
          product: context.props.product
            ? {
                id: context.props.product.sku.toString(),
                name: context.props.product.title,
              }
            : { id: undefined },
          view: {
            currency,
            type: QubitViewType.PRODUCT,
            subtypes: context.props.subTypes || [],
            language: defaultLocale,
          },
        },
        resolveVisitorState: true,
        recsPreFilter: false,
      },
    })

    const qubitContent =
      qubitData?.qubitPlacements?.content?.[
        PlacementContentType.RECOMMENDATIONS
      ]

    if (!qubitContent?.recs.length) {
      return {
        ...context.props,
        products: await fetchFallbackData(
          context.props.source,
          context.apolloClient,
        ),
        qubitCallbackData: qubitData?.qubitPlacements?.callbackData,
        cache: {
          ttl: 0,
        },
      }
    }

    const skus = qubitContent.recs.map((product) => product.id)
    const nextConfig = getConfig() as NextConfig
    const config = await loadConfiguration(nextConfig, context.req)

    const { data } = await context.apolloClient.query<
      {
        productBlockList: ProductBlockListData
      },
      ProductBlockListQueryVariables & ProductBlockListFeatureArgs
    >({
      query: QUBIT_PRODUCTS_QUERY,
      variables: {
        skus,
        currency: (context.req.config.sessionSettings?.currency ||
          'GBP') as Currency,
        shippingDestination: (context.req.config.sessionSettings
          ?.shippingDestination || 'GB') as Country,
        vipPriceEnabled:
          context.config.publicRuntimeConfig.siteConfig.enableVipPrice &&
          (context.req.horizonFeatures || []).includes(
            Feature.VipPricingEnabled,
          ),
        productContentKeys: config.productBlockContentKeys,
      },
    })

    return {
      ...context.props,
      title: qubitContent.headline ?? context.props.title,
      products: data.productBlockList,
      qubitCallbackData: qubitData.qubitPlacements.callbackData,
      cache: {
        ttl: 0,
      },
    }
  } catch (error) {
    console.error(`Failed fetching qubit recs data with error: ${error}`)

    try {
      return {
        ...context.props,
        products: await fetchFallbackData(
          context.props.source,
          context.apolloClient,
        ),
        qubitCallbackData: undefined,
        cache: {
          ttl: 0,
        },
      }
    } catch (fallbackError) {
      console.error(
        `Failed fetching qubit fallback products with error: ${fallbackError}`,
      )

      return {
        ...context.props,
        products: [],
        qubitCallbackData: undefined,
        cache: {
          ttl: 0,
        },
      }
    }
  }
})

const ESIQubitRecommendations = withESIWrapper(QubitRecommendations)

export { ESIQubitRecommendations as QubitRecommendations }

export default ESIQubitRecommendations
